import React, { useMemo, useState } from 'react'
import { formatFleetValue } from '../fleetLiveHelpers'

const WIDTH = 200
const HEIGHT = 32
const PAD_Y = 3
// Raw series has up to ~720 noisy points; draw time-bucket means so the trend reads as a line
const DRAW_BUCKETS = 60

// The last point stays raw so the line ends on the latest reading
const downsample = (points) => {
  if (points.length <= DRAW_BUCKETS) return points
  const last = points[points.length - 1]
  const t0 = points[0].timestamp
  const step = (last.timestamp - t0) / (DRAW_BUCKETS - 1) || 1
  const buckets = []
  points.slice(0, -1).forEach((p) => {
    const i = Math.min(Math.floor((p.timestamp - t0) / step), DRAW_BUCKETS - 2)
    const b = buckets[i] || (buckets[i] = { t: 0, v: 0, n: 0 })
    b.t += p.timestamp
    b.v += p.value
    b.n += 1
  })
  return [...buckets.filter(Boolean).map((b) => ({ timestamp: b.t / b.n, value: b.v / b.n })), last]
}

const formatTime = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })

/** Last-hour mini chart for a blender field. Points: { timestamp, value }[] ascending. */
const FleetSparkline = ({ points = [], color = '#3fc6e8', unit, decimals = 1 }) => {
  const [hoverIndex, setHoverIndex] = useState(null)

  const geometry = useMemo(() => {
    if (points.length < 2) return null
    const t0 = points[0].timestamp
    const t1 = points[points.length - 1].timestamp
    const drawn = downsample(points)
    let min = Infinity
    let max = -Infinity
    drawn.forEach((p) => {
      if (p.value < min) min = p.value
      if (p.value > max) max = p.value
    })
    const span = max - min || Math.abs(max) || 1
    const lo = max === min ? min - span / 2 : min
    const tSpan = t1 - t0 || 1
    const x = (t) => ((t - t0) / tSpan) * WIDTH
    const y = (v) => HEIGHT - PAD_Y - ((v - lo) / span) * (HEIGHT - PAD_Y * 2)
    const line = drawn.map((p) => `${x(p.timestamp).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ')
    return { x, y, t0, tSpan, line, drawn }
  }, [points])

  if (!geometry) {
    return <div className="fdSparkline fdSparkline--empty" />
  }

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.min(Math.max((e.clientX - rect.left) / (rect.width || 1), 0), 1)
    const target = geometry.t0 + ratio * geometry.tSpan
    // Snap to the drawn points so the marker sits on the line
    const { drawn } = geometry
    let lo = 0
    let hi = drawn.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (drawn[mid].timestamp < target) lo = mid + 1
      else hi = mid
    }
    const nearest = lo > 0 && target - drawn[lo - 1].timestamp < drawn[lo].timestamp - target ? lo - 1 : lo
    setHoverIndex(nearest)
  }

  const hovered = hoverIndex != null ? geometry.drawn[hoverIndex] : null
  const hoverLeft = hovered ? (geometry.x(hovered.timestamp) / WIDTH) * 100 : 0
  const hoverTop = hovered ? (geometry.y(hovered.value) / HEIGHT) * 100 : 0

  return (
    <div
      className="fdSparkline"
      onMouseMove={handleMove}
      onMouseLeave={() => setHoverIndex(null)}
    >
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
        <polyline
          points={geometry.line}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {hovered ? (
          <line
            x1={geometry.x(hovered.timestamp)}
            x2={geometry.x(hovered.timestamp)}
            y1="0"
            y2={HEIGHT}
            className="fdSparklineCursor"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>
      {hovered ? (
        <>
          <span
            className="fdSparklineDot"
            style={{ left: `${hoverLeft}%`, top: `${hoverTop}%`, backgroundColor: color }}
          />
          {/* translateX by the same % keeps the tip inside the chart at both edges */}
          <div
            className="fdSparklineTip"
            style={{ left: `${hoverLeft}%`, transform: `translateX(-${hoverLeft}%)` }}
          >
            <b>{formatFleetValue(hovered.value, decimals)}</b>
            {unit ? <span className="unit">{unit}</span> : null}
            <span className="time">{formatTime(hovered.timestamp)}</span>
          </div>
        </>
      ) : null}
    </div>
  )
}

export default React.memo(FleetSparkline)
